'use client';

import { HStack, VStack } from '@terra/ui-styled-system';
import { MetaDefault, MonoSmall } from '../../../../components/typography';
import { useTraceEntityNavigation } from '../context/TraceEntityNavigationContext';
import type { InlineToolEntityListItem } from '../utils/inline-tool-entity-list';
import {
  TRACE_INLINE_ROW_MIN_HEIGHT,
  TRACE_INLINE_SCROLL_MAX_HEIGHT,
} from '../utils/trace-inline-layout';
import { TraceInlineOpenEntityAction } from './TraceInlineOpenEntityAction';

type TraceInlineEntityListProps = {
  items: InlineToolEntityListItem[];
};

export function TraceInlineEntityList({ items }: TraceInlineEntityListProps) {
  const onOpenEntity = useTraceEntityNavigation();
  const hasMethods = items.some((item) => Boolean(item.method));

  return (
    <VStack
      alignItems="stretch"
      gap={0.5}
      maxH={TRACE_INLINE_SCROLL_MAX_HEIGHT}
      overflowY="auto"
      data-trace-inline-entity-list
    >
      {items.map((item) => (
        <HStack
          key={item.key}
          className="group"
          gap={2.5}
          minH={TRACE_INLINE_ROW_MIN_HEIGHT}
          px={2}
          py={0.5}
          borderRadius="sm"
          alignItems="center"
          minW={0}
          _hover={{ bg: 'bg.subtle' }}
        >
          {hasMethods ? (
            <MonoSmall color="fg.muted" flexShrink={0} minW="44px">
              {item.method ?? ''}
            </MonoSmall>
          ) : null}
          <MonoSmall
            color="fg.default"
            flexShrink={1}
            minW={0}
            maxW="50%"
            truncate
            title={item.url}
          >
            {item.location}
          </MonoSmall>
          <MetaDefault
            color="fg.subtle"
            flex={1}
            minW={0}
            truncate
            title={item.description}
          >
            {item.description ?? ''}
          </MetaDefault>
          {item.id && onOpenEntity ? (
            <TraceInlineOpenEntityAction
              target={{
                type: item.type,
                id: item.id,
                label: item.method
                  ? `${item.method} ${item.location}`
                  : item.location,
              }}
              onOpenEntity={onOpenEntity}
            />
          ) : null}
        </HStack>
      ))}
    </VStack>
  );
}
